/**
 * 表格行操作（2026-10-21）：一份 actions 定义，同时用于
 *   1) 紧凑操作列：<RowActions actions={...} />  —— 1~2 个主操作直接显示，其余收进「⋯」
 *   2) 行右键菜单：useRowContextMenu(getActions) —— 右键 / 触屏长按（~500ms）在光标处弹出同一组操作
 *
 * action: { key, label, icon, onClick, danger, disabled, hidden, inline, primary, loading, tooltip,
 *           confirm: true | { title, content, okText, cancelText, okType } }
 *   - inline: 在操作列直接显示（未标注任何 inline 时取第一个）；最多 maxInline 个
 *   - confirm: 点击时先 Modal.confirm 再执行 onClick
 *   - { type: 'divider' } 可插分隔线（仅菜单中生效）
 *
 * 用法：
 *   const getActions = (r) => [...];
 *   const rowMenu = useRowContextMenu(getActions);
 *   <Table onRow={rowMenu.onRow(rowLink(navigate, ...))} ... />
 *   {rowMenu.menu}
 *   列：{ title: t('Actions'), key: 'action', render: (_, r) => <RowActions actions={getActions(r)} /> }
 *
 * 规则：Shift+右键 永远是浏览器原生菜单；在链接/输入框上右键、或已选中文字时也走原生菜单。
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Button, Dropdown, Modal, Popconfirm, Tooltip } from 'antd';
import { MoreOutlined } from '@ant-design/icons';
import { useTranslation } from '../i18n';
import './RowActions.css';

const NATIVE_MENU_TARGETS = 'a[href],input,textarea,select,[contenteditable="true"],[contenteditable=""],.allow-native-contextmenu';
const LONG_PRESS_MS = 500;
const MOVE_TOLERANCE = 10;

const visible = (actions) => (actions || []).filter((a) => a && !a.hidden);

/** 执行一个 action（带 confirm） */
export const runAction = (action, t) => {
    if (!action || action.disabled || typeof action.onClick !== 'function') return;
    if (action.confirm) {
        const c = action.confirm === true ? {} : action.confirm;
        Modal.confirm({
            title: c.title || action.label,
            content: c.content,
            okText: c.okText || (t ? t('OK') : 'OK'),
            cancelText: c.cancelText || (t ? t('Cancel') : 'Cancel'),
            okType: c.okType || (action.danger ? 'danger' : 'primary'),
            okButtonProps: (c.okType || (action.danger ? 'danger' : '')) === 'danger' ? { danger: true } : undefined,
            onOk: () => action.onClick(),
        });
        return;
    }
    action.onClick();
};

/** actions → antd Menu items（隐藏项去掉、首个危险项前自动加分隔线、合并多余分隔线） */
export const toMenuItems = (actions) => {
    const items = [];
    let sawNormal = false;
    let dividerAdded = false;
    visible(actions).forEach((a, i) => {
        if (a.type === 'divider') {
            if (items.length && items[items.length - 1].type !== 'divider') items.push({ type: 'divider', key: `d${i}` });
            return;
        }
        if (a.danger && sawNormal && !dividerAdded && items[items.length - 1]?.type !== 'divider') {
            items.push({ type: 'divider', key: `dd${i}` });
            dividerAdded = true;
        }
        if (!a.danger) sawNormal = true;
        items.push({ key: a.key, label: a.label, icon: a.icon, danger: !!a.danger, disabled: !!a.disabled || !!a.loading });
    });
    while (items.length && items[items.length - 1].type === 'divider') items.pop();
    return items;
};

const menuFor = (actions, t, after) => {
    const list = visible(actions);
    return {
        items: toMenuItems(list),
        onClick: ({ key, domEvent }) => {
            if (domEvent) domEvent.stopPropagation();
            if (after) after();
            runAction(list.find((a) => a.key === key), t);
        },
    };
};

/** 紧凑操作列 */
const RowActions = ({ actions, maxInline = 2, size = 'small' }) => {
    const { t } = useTranslation();
    const list = visible(actions).filter((a) => a.type !== 'divider');
    if (!list.length) return null;

    let inline = list.filter((a) => a.inline).slice(0, maxInline);
    if (!inline.length) inline = list.slice(0, 1);
    const rest = list.filter((a) => !inline.includes(a));

    return (
        // no-row-link：点击操作列不触发整行跳转
        <span className="row-actions no-row-link" onClick={(e) => e.stopPropagation()}>
            {inline.map((a) => {
                const btn = (
                    <Button
                        key={a.key}
                        type={a.primary ? 'primary' : 'link'}
                        size={size}
                        danger={!!a.danger}
                        disabled={!!a.disabled}
                        loading={!!a.loading}
                        icon={a.icon}
                        onClick={a.confirm ? undefined : () => runAction(a, t)}
                    >
                        {a.label}
                    </Button>
                );
                const tipped = a.tooltip ? <Tooltip key={a.key} title={a.tooltip}>{btn}</Tooltip> : btn;
                if (!a.confirm) return tipped;
                // 操作列里的确认用 Popconfirm（右键菜单里用 Modal.confirm）
                const c = a.confirm === true ? {} : a.confirm;
                return (
                    <Popconfirm
                        key={a.key}
                        title={c.title || a.label}
                        description={c.content}
                        okText={c.okText || t('OK')}
                        cancelText={c.cancelText || t('Cancel')}
                        okButtonProps={{ danger: (c.okType || (a.danger ? 'danger' : '')) === 'danger' }}
                        disabled={!!a.disabled}
                        onConfirm={() => a.onClick()}
                    >
                        {tipped}
                    </Popconfirm>
                );
            })}
            {rest.length > 0 && (
                <Dropdown trigger={['click']} menu={menuFor(rest, t)} placement="bottomRight">
                    <Button type="text" size={size} icon={<MoreOutlined />} aria-label={t('More actions')} />
                </Dropdown>
            )}
        </span>
    );
};

/**
 * 行右键 / 长按菜单。
 * @param getActions (record) => actions
 * @param opts.rowKey 行主键（string | fn），用于高亮当前右键行，默认 'id'
 * @returns { onRow(baseOnRow?) => antd onRow, menu: ReactNode（渲染一次）, close }
 */
export const useRowContextMenu = (getActions, opts = {}) => {
    const { t } = useTranslation();
    const { rowKey = 'id' } = opts;
    const [state, setState] = useState(null); // { x, y, record }
    const pressRef = useRef(null); // { timer, x, y }
    const suppressClickRef = useRef(false);
    const getActionsRef = useRef(getActions);
    getActionsRef.current = getActions;

    const keyOf = useCallback((r) => (typeof rowKey === 'function' ? rowKey(r) : r?.[rowKey]), [rowKey]);
    const close = useCallback(() => setState(null), []);

    const openAt = useCallback((x, y, record) => {
        if (!toMenuItems(getActionsRef.current(record)).length) return false;
        setState({ x, y, record });
        return true;
    }, []);

    // 关闭：滚动 / Esc / 点外部 / 窗口变化
    useEffect(() => {
        if (!state) return undefined;
        const onKey = (e) => { if (e.key === 'Escape') close(); };
        const onDown = (e) => {
            const el = e.target;
            if (el && el.closest && el.closest('.row-ctx-menu-overlay')) return;
            close();
        };
        const onScroll = (e) => {
            const el = e.target;
            if (el && el.closest && el.closest('.row-ctx-menu-overlay')) return;
            close();
        };
        document.addEventListener('keydown', onKey, true);
        document.addEventListener('mousedown', onDown, true);
        document.addEventListener('touchstart', onDown, true);
        window.addEventListener('scroll', onScroll, true);
        window.addEventListener('resize', close);
        window.addEventListener('blur', close);
        return () => {
            document.removeEventListener('keydown', onKey, true);
            document.removeEventListener('mousedown', onDown, true);
            document.removeEventListener('touchstart', onDown, true);
            window.removeEventListener('scroll', onScroll, true);
            window.removeEventListener('resize', close);
            window.removeEventListener('blur', close);
        };
    }, [state, close]);

    const cancelPress = () => {
        if (pressRef.current) clearTimeout(pressRef.current.timer);
        pressRef.current = null;
    };

    const onRow = useCallback((baseOnRow) => (record, index) => {
        const base = (baseOnRow ? baseOnRow(record, index) : null) || {};
        const active = state && keyOf(state.record) === keyOf(record);
        return {
            ...base,
            className: [base.className, active ? 'row-ctx-active' : ''].filter(Boolean).join(' ') || undefined,
            onContextMenu: (e) => {
                if (base.onContextMenu) base.onContextMenu(e);
                if (e.defaultPrevented || e.shiftKey) return;
                const el = e.target;
                if (el && el.closest && el.closest(NATIVE_MENU_TARGETS)) return;
                const sel = window.getSelection ? String(window.getSelection() || '').trim() : '';
                if (sel) return;
                if (openAt(e.clientX, e.clientY, record)) e.preventDefault();
            },
            onTouchStart: (e) => {
                if (base.onTouchStart) base.onTouchStart(e);
                cancelPress();
                const tch = e.touches && e.touches[0];
                if (!tch || e.touches.length > 1) return;
                const el = e.target;
                if (el && el.closest && el.closest(NATIVE_MENU_TARGETS)) return;
                const x = tch.clientX;
                const y = tch.clientY;
                pressRef.current = {
                    x, y,
                    timer: setTimeout(() => {
                        pressRef.current = null;
                        if (openAt(x, y, record)) {
                            suppressClickRef.current = true;
                            setTimeout(() => { suppressClickRef.current = false; }, 800);
                        }
                    }, LONG_PRESS_MS),
                };
            },
            onTouchMove: (e) => {
                if (base.onTouchMove) base.onTouchMove(e);
                const p = pressRef.current;
                const tch = e.touches && e.touches[0];
                if (p && tch && (Math.abs(tch.clientX - p.x) > MOVE_TOLERANCE || Math.abs(tch.clientY - p.y) > MOVE_TOLERANCE)) cancelPress();
            },
            onTouchEnd: (e) => {
                if (base.onTouchEnd) base.onTouchEnd(e);
                cancelPress();
                // 长按已弹菜单：吞掉随后合成的 click（避免进详情）
                if (suppressClickRef.current && e.cancelable) e.preventDefault();
            },
            onTouchCancel: (e) => {
                if (base.onTouchCancel) base.onTouchCancel(e);
                cancelPress();
            },
            onClick: (e) => {
                if (suppressClickRef.current) {
                    suppressClickRef.current = false;
                    return;
                }
                if (base.onClick) base.onClick(e);
            },
        };
    }, [state, keyOf, openAt]);

    useEffect(() => () => cancelPress(), []);

    const menu = state ? (
        <Dropdown
            open
            menu={menuFor(getActionsRef.current(state.record), t, close)}
            overlayClassName="row-ctx-menu-overlay"
            placement="bottomLeft"
            autoAdjustOverflow
            onOpenChange={(o) => { if (!o) close(); }}
            trigger={['contextMenu']}
        >
            <span
                className="row-ctx-menu-anchor"
                style={{ left: state.x, top: state.y }}
            />
        </Dropdown>
    ) : null;

    return { onRow, menu, close };
};

export default RowActions;
